import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Target, TrendingUp, Building2, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';

const ValueProposition: React.FC = () => {
  const navigate = useNavigate();
  const { getLocalizedPath } = useLanguage();
  
  const propositions = [
    {
      icon: Target,
      title: 'For Patients',
      subtitle: 'Find Your Perfect Match',
      description: 'Advanced AI matching combined with human expertise to connect you with the right therapist.',
      features: [
        '5,000+ certified professionals',
        'Sessions from $25',
        'Available in 40+ languages'
      ],
      cta: 'Start Your Journey',
      ctaAction: () => navigate(getLocalizedPath('/find')),
      gradient: 'from-blue-500/10 to-cyan-500/10',
      highlighted: true
    },
    {
      icon: Building2,
      title: 'For Companies',
      subtitle: 'Corporate Wellness',
      description: 'Comprehensive mental health programs tailored for your team\'s wellbeing and productivity.',
      features: [
        'Custom wellness plans',
        'Confidential sessions',
        'ROI tracking & analytics'
      ],
      cta: 'Get Quote',
      ctaAction: () => navigate(getLocalizedPath('/contact')),
      gradient: 'from-purple-500/10 to-pink-500/10'
    }
  ];
  
  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Mental Health Solutions for Everyone
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Whether you're seeking personal support or supporting your team
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {propositions.map((prop, index) => {
            const Icon = prop.icon;
            return (
              <Card 
                key={index}
                className={`relative overflow-hidden transition-all hover:shadow-xl ${
                  prop.highlighted ? 'border-2 border-primary shadow-lg scale-105' : ''
                }`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${prop.gradient} opacity-50`} />
                
                <CardHeader className="relative">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <CardTitle className="text-2xl">{prop.title}</CardTitle>
                  <CardDescription className="text-lg font-semibold text-foreground">
                    {prop.subtitle}
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="relative space-y-6">
                  <p className="text-muted-foreground">
                    {prop.description}
                  </p>
                  
                  <ul className="space-y-2">
                    {prop.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    onClick={prop.ctaAction}
                    variant={prop.highlighted ? 'default' : 'outline'}
                    className="w-full group"
                  >
                    {prop.cta}
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ValueProposition;
